import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiArrowLeft, HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { PageContent } from '@/components/layout/PageLayout';
import DetailHeader from '@/components/layout/DetailHeader';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useGetLegalProfessionalDetailQuery } from '@/store/legalProfessionalsApi';
import { StatusBadge } from './LegalProfessionalDirectoryPage.styled';
import {
  DetailGrid,
  DetailCard,
  DetailCardTitle,
  DetailRow,
  DetailLabel,
  DetailValue,
  TagList,
  Tag,
  WorkplaceItem,
  WorkplaceKind,
  WorkplaceDetails,
} from './LegalProfessionalDetailPage.styled';

export default function LegalProfessionalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, isLoading } = useGetLegalProfessionalDetailQuery(id!, { skip: !id });

  if (isLoading) {
    return (
      <PageContent>
        <LoadingSpinner />
      </PageContent>
    );
  }

  if (!data) {
    return (
      <PageContent>
        <p>{t('legalDirectory.detail.notFound')}</p>
        <Button variant="outline-primary" onClick={() => navigate(-1)}>
          <HiArrowLeft className="me-1" /> {t('common.back')}
        </Button>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <DetailHeader
        onBack={() => navigate(-1)}
        title={data.fullName}
        meta={
          <>
            <span>
              {t(`legalDirectory.types.${data.professionalType === 'Adwokat' ? 'adwokat' : data.professionalType === 'RadcaPrawny' ? 'radcaPrawny' : data.professionalType === 'AplikantAdwokacki' ? 'aplikantAdwokacki' : 'aplikantRadcowski'}`)}
            </span>
            {data.status && (
              <StatusBadge $status={data.status}>
                {t(`legalDirectory.statuses.${data.status.toLowerCase()}`)}
              </StatusBadge>
            )}
          </>
        }
      />

      <DetailGrid>
        <DetailCard>
          <DetailCardTitle>{t('legalDirectory.detail.personalInfo')}</DetailCardTitle>
          <DetailRow>
            <DetailLabel>{t('legalDirectory.detail.firstName')}</DetailLabel>
            <DetailValue>{data.firstNames}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('legalDirectory.detail.lastName')}</DetailLabel>
            <DetailValue>{data.lastName}</DetailValue>
          </DetailRow>
          {data.middleName && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.detail.middleName')}</DetailLabel>
              <DetailValue>{data.middleName}</DetailValue>
            </DetailRow>
          )}
          <DetailRow>
            <DetailLabel>{t('legalDirectory.table.registryNumber')}</DetailLabel>
            <DetailValue>{data.registryNumber}</DetailValue>
          </DetailRow>
          {data.oldRegistryNumber && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.detail.oldRegistryNumber')}</DetailLabel>
              <DetailValue>{data.oldRegistryNumber}</DetailValue>
            </DetailRow>
          )}
          {data.registryEntryDate && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.detail.registryEntryDate')}</DetailLabel>
              <DetailValue>{data.registryEntryDate}</DetailValue>
            </DetailRow>
          )}
          {data.deputy && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.detail.deputy')}</DetailLabel>
              <DetailValue>{data.deputy}</DetailValue>
            </DetailRow>
          )}
        </DetailCard>

        <DetailCard>
          <DetailCardTitle>{t('legalDirectory.detail.contactInfo')}</DetailCardTitle>
          <DetailRow>
            <DetailLabel>{t('legalDirectory.table.city')}</DetailLabel>
            <DetailValue>{data.city ?? '—'}</DetailValue>
          </DetailRow>
          {data.email && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.detail.email')}</DetailLabel>
              <DetailValue>
                <a href={`mailto:${data.email}`}>{data.email}</a>
              </DetailValue>
            </DetailRow>
          )}
          {data.correspondenceAddress && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.detail.address')}</DetailLabel>
              <DetailValue>{data.correspondenceAddress}</DetailValue>
            </DetailRow>
          )}
          {data.barAssociationName && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.filters.barAssociation')}</DetailLabel>
              <DetailValue>{data.barAssociationName}</DetailValue>
            </DetailRow>
          )}
          {data.profileUrl && (
            <DetailRow>
              <DetailLabel>{t('legalDirectory.detail.profile')}</DetailLabel>
              <DetailValue>
                <a href={data.profileUrl} target="_blank" rel="noopener noreferrer">
                  {t('legalDirectory.detail.viewProfile')} <HiArrowTopRightOnSquare />
                </a>
              </DetailValue>
            </DetailRow>
          )}
        </DetailCard>
      </DetailGrid>

      {data.workplaces.length > 0 && (
        <DetailCard $spaced>
          <DetailCardTitle>{t('legalDirectory.detail.workplaces')}</DetailCardTitle>
          {data.workplaces.map((wp, i) => (
            <WorkplaceItem key={i}>
              <WorkplaceKind>{wp.kind}</WorkplaceKind>
              <WorkplaceDetails>{wp.details}</WorkplaceDetails>
            </WorkplaceItem>
          ))}
        </DetailCard>
      )}

      {data.preferredPractices.length > 0 && (
        <DetailCard>
          <DetailCardTitle>{t('legalDirectory.detail.practiceAreas')}</DetailCardTitle>
          <TagList>
            {data.preferredPractices.map((practice) => (
              <Tag key={practice}>{practice}</Tag>
            ))}
          </TagList>
        </DetailCard>
      )}
    </PageContent>
  );
}
